<nz-card class="card">
  <h5>Issue Overview:</h5>
  <p *ngIf="enclosureBk">
    <b
      >{{ storageLevelV2.String | levelIdentifierToLevelName | titlecase }} Location:
      {{ enclosureBk | componentNameTranslation }} &rarr; {{ componentBk | componentNameTranslation }}</b
    >
  </p>
  <p>
    <b>{{ componentBk | componentNameTranslation }} is experiencing a SoC imbalance,</b> resulting in uneven energy distribution
    across {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }}. This imbalance can lead to several performance and
    longevity issues:
  </p>
  <ul class="list-disc">
    <li>
      Reduced <b>usable energy</b> as weaker {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }} limit discharge
      depth.
    </li>
    <li>
      Accelerated <b>aging of specific {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }},</b> particularly under
      high strain or overcharge scenarios.
    </li>
    <li>
      Potential limitations on {{ storageLevelV2.System | levelIdentifierToLevelName }} availability if imbalance breaches safety
      thresholds enforced by the BMS.
    </li>
  </ul>
  <br />
  <p>
    Such imbalances are critical to monitor and resolve, as they can gradually degrade both short-term performance and long-term
    asset value.
  </p>
</nz-card>
<nz-card class="card">
  <h5>Why This Matters for You:</h5>
  <ul class="list-disc">
    <li><b>Improved financial performance</b> by recovering lost capacity and minimize downtime.</li>
    <li><b>Preserve component health</b> across the fleet by addressing stress factors early.</li>
  </ul>
  <br />
  <p>
    Proactive balancing not only boosts {{ storageLevelV2.System | levelIdentifierToLevelName }} efficiency but also strengthens
    your ability to manage claims regarding underperformance towards your technology providers.
  </p>
</nz-card>
@if (hasSiblingEffect) {
  <nz-card class="card">
    <h5>Sibling effect</h5>
    <p>
      The sibling effect occurs within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }}. This means, that the usable energy of this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is determined by its worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}. The usable energy of the
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is calculated by the usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} multiplied by the number of sibling
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} (and not by the sum of usable energy of individual
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s).
    </p>
    <p>
      TWAICE recommends the "quick fix" to take the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline if the remaining usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} is less than the energy that would be recovered in the
      whole {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} by taking the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline plus an additional {{ quickFixBufferPercent }}%
      buffer.
    </p>
    <p>
      In this case, the difference is <b>below</b> the buffer of {{ quickFixBufferPercent }}%. We <b>don't recommend</b> the quick
      fix.
    </p>
  </nz-card>
}
<nz-card class="card">
  <h5>Recommendations:</h5>
  <div [ngSwitch]="recommendation">
    <p *ngSwitchCase="Recommendation.Monitor">
      <b>Routine monitoring is recommended</b> to establish early awareness and trend visibility of developing imbalances. The
      performance limitation is currently limited, but can develop into a noticeable issue if not monitored. Keep an eye on the
      component.
    </p>
    <p *ngSwitchCase="Recommendation.Inspect">
      <b>Targeted inspection and diagnostic verification are recommended</b> to assess imbalance causes and prepare for potential
      corrective actions. The performance limitation due to the component is not yet significant enough to justify a immediate
      balancing process. Nevertheless, with imbalances increasing, the time to balance the components also increases.
    </p>
    <div *ngSwitchDefault>
      <p>
        <b>{{ storageLevelV2.Cell | levelIdentifierToLevelName | titlecase }} balancing</b> is recommended to restore even energy
        levels across the {{ storageLevelV2.String | levelIdentifierToLevelName }}. Performing corrective balancing can:
      </p>
      <ul class="list-disc">
        <li>
          <b>Recover approximately {{ recoverableEnergy | MWhFormatter }} of energy,</b> currently considered locked due to
          imbalance.
        </li>
        <li>
          <b>Extend the operational life</b> of weaker {{ storageLevelV2.Cell | storageLevelToCustomerLevelName: false }} by
          reducing additional stress during cycles.
        </li>
        <li>
          Ensure {{ storageLevelV2.System | levelIdentifierToLevelName }}-level safety compliance, avoiding premature derating or
          automated shutdowns triggered by imbalance alarms.
        </li>
      </ul>
    </div>
  </div>
</nz-card>
<nz-card class="card">
  <h5>Documenting the issue:</h5>
  <p>To facilitate a timely and informed resolution, take the following actions:</p>
  <ol class="list-decimal">
    <li><b>Export a CSV report</b> of the SoC and voltage spread data for {{ componentBk | componentNameTranslation }}.</li>
    <li>
      <b>Capture screenshots</b> from the <b>Performance State of Charge</b> page, including:
      <ul class="list-disc">
        <li>SoC heatmaps over the {{ storageLevelV2.System | levelIdentifierToLevelName }}</li>
        <li>SoC spread over time</li>
        <li>
          {{ storageLevelV2.String | levelIdentifierToLevelName | titlecase }}-to-{{
            storageLevelV2.String | levelIdentifierToLevelName | titlecase
          }}
          SoC comparisons
        </li>
      </ul>
    </li>
    <li>
      <b>Prepare a short summary report</b> combining your observations with the data in the platform. This will provide O&M teams
      or {{ storageLevelV2.System | levelIdentifierToLevelName }} partners with a clear foundation for action planning or
      escalation.
    </li>
  </ol>
  <br />
  <p>This documentation ensures traceability and provides a clear timeline of when the issue emerged and how it evolved.</p>
</nz-card>
<nz-card class="card">
  <h5>Guidance for Collaboration with Service and Hardware Providers:</h5>
  <p>To ensure constructive and efficient collaboration with your internal and external stakeholders:</p>
  <ul class="list-disc">
    <li>
      Share the <b>CSV file and screenshots</b> (e.g., heatmaps or plots from Data Explorer) showing the current extent and
      evolution of the imbalance.
    </li>
    <li>
      Document the <b>{{ recoverableEnergy | MWhFormatter }} of recoverable capacity</b> as a quantifiable impact on
      {{ storageLevelV2.System | levelIdentifierToLevelName }}
      efficiency and revenue.
    </li>
    <li>
      Include any historical trends that suggest <b>incomplete balancing routines or repeated imbalance events,</b> which may
      point to configuration or hardware issues.
    </li>
    <li>
      Ask for input on whether <b>enhanced balancing algorithms</b> or <b>firmware updates</b> might be applicable for this
      {{ storageLevelV2.String | levelIdentifierToLevelName }} type.
    </li>
  </ul>
</nz-card>
